/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import ModalLoadingState from "../PopUpModal/ModalLoadingState";
import { PebbleTable } from "../ui/TableComponents";
import ResponsiveTabs from "../ui/ResponsiveTabs";
import picture_placeholder from "../../assets/images/no_image_placeholder.jpg"
import { useNavigate, useLocation } from "react-router-dom";
import VendorPaymentModal from "./VendorPayment/VendorPaymentModal";
import { eventDiscoveryService } from "../../services/EventDiscoveryService";
import { SearchIcon, FilterIcon, SparklesIcon, LocationMarkerIcon, CalendarIcon, ClockIcon, CheckCircleIcon } from "@heroicons/react/outline";
import { XIcon } from "@heroicons/react/solid";
import LocationAutocomplete from "../ui/LocationAutocomplete";

const formatRelativeDate = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffDays = Math.round((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) return `${Math.round(diffDays / 30)} months ago`;
  return `${Math.round(diffDays / 365)} year${Math.round(diffDays / 365) > 1 ? "s" : ""} ago`;
};

const HistoryTab = ({ pastEvents, loading, onViewEvent }) => {
  if (loading) return <ModalLoadingState />;

  const attended = pastEvents.filter(e => e.status === "Approved" || e.status === "Registered");
  const totalFees = attended.reduce((sum, e) => sum + (e.registrationFee || 0), 0);
  const cities = [...new Set(attended.map(e => e.city).filter(Boolean))];

  if (pastEvents.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 bg-grey-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ClockIcon className="w-8 h-8 text-grey-400" />
        </div>
        <h3 className="text-lg font-semibold text-grey-900 mb-2">No event history yet</h3>
        <p className="text-sm text-grey-500">Events you apply to and attend will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-grey-200 p-4 text-center">
          <p className="text-2xl font-bold text-grey-900">{attended.length}</p>
          <p className="text-xs text-grey-500 mt-1">Events attended</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4 text-center">
          <p className="text-2xl font-bold text-grey-900">${totalFees.toLocaleString()}</p>
          <p className="text-xs text-grey-500 mt-1">Total fees paid</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4 text-center">
          <p className="text-2xl font-bold text-grey-900">{cities.length}</p>
          <p className="text-xs text-grey-500 mt-1">{cities.length === 1 ? "City" : "Cities"}</p>
        </div>
      </div>

      {/* Event list */}
      <div className="space-y-3">
        {pastEvents.map(event => {
          const attended = event.status === "Approved" || event.status === "Registered";
          return (
            <div
              key={event.id}
              className="bg-white rounded-xl border border-grey-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => onViewEvent(event.id)}
            >
              <div className="flex gap-0">
                {/* Cover photo */}
                <div className="w-28 h-24 flex-shrink-0 overflow-hidden">
                  <img
                    src={event.image || picture_placeholder}
                    alt={event.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0 px-4 py-3 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-grey-900 text-sm leading-snug line-clamp-1">{event.name}</h3>
                      <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        attended
                          ? "bg-green-50 text-green-700"
                          : "bg-grey-100 text-grey-600"
                      }`}>
                        {attended ? <><CheckCircleIcon className="w-3 h-3" />Attended</> : "Declined"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-grey-500">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-3.5 h-3.5" />
                        {event.date?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      {event.city && (
                        <span className="flex items-center gap-1">
                          <LocationMarkerIcon className="w-3.5 h-3.5" />
                          {event.city}{event.state ? `, ${event.state}` : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-2">
                    {attended && event.boothNumber && (
                      <span className="text-xs text-grey-600">
                        <span className="font-medium">Booth</span> #{event.boothNumber}
                      </span>
                    )}
                    {attended && event.registrationFee > 0 && (
                      <span className="text-xs text-grey-600">
                        <span className="font-medium">${event.registrationFee}</span> paid
                      </span>
                    )}
                    <span className="text-xs text-grey-400 ml-auto">{formatRelativeDate(event.date)}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function EventContents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const MPSProfileID = user.uid;
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || "discover");
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [goodFitEvents, setGoodFitEvents] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [mpsProfile, setMpsProfile] = useState(null);
  const [pastEvents, setPastEvents] = useState([]);
  const [pastEventsLoading, setPastEventsLoading] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: "all",
    location: "",
    showGoodFitOnly: false,
    dateRange: "all",
    feeRange: "all",
    eventSize: "all",
    deadlineRange: "all",
  });

  const loadAllEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await eventDiscoveryService.discoverEvents(MPSProfileID);
      
      if (result.success) {
        setMpsProfile(result.mpsProfile);
        
        const formatEvents = (eventsList) => eventsList.map((event) => ({
          ...event,
          image: event?.image || picture_placeholder,
          action: "View",
          rawDate: event.date,
          rawDeadline: event.applicationDeadline,
          applicationDeadline: event.applicationDeadline?.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
          }),
          date: event.date?.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
          })
        }));
        
        setGoodFitEvents(formatEvents(result.goodFitEvents));
        setEvents(formatEvents(result.allEvents));
      }
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setIsLoading(false);
    }
  }, [MPSProfileID]);

  useEffect(() => {
    loadAllEvents();
  }, [loadAllEvents]);

  useEffect(() => {
    if (location.state?.refresh) {
      loadAllEvents();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, loadAllEvents, navigate, location.pathname]);

  useEffect(() => {
    if (activeTab === "history" && pastEvents.length === 0) {
      const loadHistory = async () => {
        setPastEventsLoading(true);
        try {
          const result = await eventDiscoveryService.getPastEventHistory(MPSProfileID);
          if (result.success) setPastEvents(result.pastEvents);
        } catch (err) {
          console.error("Error loading event history:", err);
        } finally {
          setPastEventsLoading(false);
        }
      };
      loadHistory();
    }
  }, [activeTab, MPSProfileID, pastEvents.length]);

  const tabs = [
    { label: "Discover", value: "discover", icon: SparklesIcon },
    { label: "All Events", value: "all" },
    { label: "Applied", value: "Applied" },
    { label: "Approved", value: "Approved" },
    { label: "Registered", value: "Registered" },
    { label: "Declined", value: "Declined" },
    { label: "History", value: "history", icon: ClockIcon },
  ];
  const activeTabLabel = tabs.find((tab) => tab.value === activeTab)?.label || "";

  const filterEvents = (eventsList) => {
    let filtered = eventsList.filter((event) => {
      if (activeTab === "discover" || activeTab === "all") return true;
      if (activeTab === "Applied") return event.status === "Applied";
      if (activeTab === "Approved") return event.status === "Approved";
      if (activeTab === "Registered") return event.status === "Registered";
      if (activeTab === "Declined") return event.status === "Declined";
      return false;
    });

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event =>
        (event.name || '').toLowerCase().includes(query) ||
        (event.location || '').toLowerCase().includes(query) ||
        (event.description || '').toLowerCase().includes(query)
      );
    }

    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(event => 
        event.sellerTypes?.some(type => 
          type.toLowerCase().includes(filters.category.toLowerCase())
        )
      );
    }

    if (filters.location) {
      const loc = filters.location.toLowerCase();
      filtered = filtered.filter(event => 
        (event.location || '').toLowerCase().includes(loc)
      );
    }

    if (filters.showGoodFitOnly) {
      filtered = filtered.filter(event => event.isGoodFit);
    }

    if (filters.dateRange && filters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filtered = filtered.filter(event => {
        const eventDate = event.rawDate;
        if (!eventDate) return true;
        
        const daysDiff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
        
        switch (filters.dateRange) {
          case 'thisWeek':
            return daysDiff >= 0 && daysDiff <= 7;
          case 'thisMonth':
            return daysDiff >= 0 && daysDiff <= 30;
          case 'next3Months':
            return daysDiff >= 0 && daysDiff <= 90;
          case 'later':
            return daysDiff > 90;
          default:
            return true;
        }
      });
    }

    if (filters.feeRange && filters.feeRange !== 'all') {
      filtered = filtered.filter(event => {
        const fee = event.vendorRegistrationFee || 0;
        switch (filters.feeRange) {
          case 'free':
            return fee === 0;
          case 'under100':
            return fee > 0 && fee <= 10000;
          case 'under250':
            return fee > 10000 && fee <= 25000;
          case 'under500':
            return fee > 25000 && fee <= 50000;
          case 'over500':
            return fee > 50000;
          default:
            return true;
        }
      });
    }

    if (filters.eventSize && filters.eventSize !== 'all') {
      filtered = filtered.filter(event => {
        const capacity = event.sellerCapacity || 0;
        switch (filters.eventSize) {
          case 'small':
            return capacity > 0 && capacity <= 20;
          case 'medium':
            return capacity > 20 && capacity <= 50;
          case 'large':
            return capacity > 50 && capacity <= 100;
          case 'xlarge':
            return capacity > 100;
          default:
            return true;
        }
      });
    }

    if (filters.deadlineRange && filters.deadlineRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filtered = filtered.filter(event => {
        const deadline = event.rawDeadline;
        if (!deadline) return true;
        
        const daysDiff = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
        
        switch (filters.deadlineRange) {
          case 'urgent':
            return daysDiff >= 0 && daysDiff <= 3;
          case 'thisWeek':
            return daysDiff >= 0 && daysDiff <= 7;
          case 'thisMonth':
            return daysDiff >= 0 && daysDiff <= 30;
          case 'later':
            return daysDiff > 30;
          default:
            return true;
        }
      });
    }

    setFilteredEvents(filtered);
  };

  useEffect(() => {
    if (events.length > 0) {
      filterEvents(events);
    }
  }, [activeTab, events, searchQuery, filters]);

  const handleViewEvent = (id) => {
    const event = events.find((e) => e.id === id);
    if (event) {
      navigate(`/mps/view-event-details/${id}`);
    }
  };

  const handlePayRegistration = (id) => {
    const event = events.find((e) => e.id === id);
    const needsPaymentNow = 
      (event?.paymentTiming === "upfront" && event?.status === "Applied" && event?.paymentStatus !== "Paid") ||
      (event?.paymentTiming !== "upfront" && event?.status === "Approved" && event?.paymentStatus !== "Paid");
    
    if (event && needsPaymentNow) {
      setSelectedApplication({
        applicationId: event.applicationId,
        eventName: event.name,
        registrationFee: event.vendorRegistrationFee || 10000
      });
      setShowPaymentModal(true);
    }
  };

  const handlePaymentClose = async (success) => {
    setShowPaymentModal(false);
    setSelectedApplication(null);
    
    if (success) {
      setIsLoading(true);
      try {
        const result = await eventDiscoveryService.discoverEvents(MPSProfileID);
        if (result.success) {
          const formatEvents = (eventsList) => eventsList.map((event) => ({
            ...event,
            image: event?.image || picture_placeholder,
            action: "View",
            rawDate: event.date,
            rawDeadline: event.applicationDeadline,
            applicationDeadline: event.applicationDeadline?.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric"
            }),
            date: event.date?.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric"
            })
          }));
          
          setGoodFitEvents(formatEvents(result.goodFitEvents));
          setEvents(formatEvents(result.allEvents));
        }
      } catch (error) {
        console.error("Error refreshing events:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const columns = [
    {
      header: "",
      key: "image",
      type: "image",
    },
    {
      header: "Event Name",
      key: "name",
      type: "compound",
      primaryKey: "name",
      secondaryKey: "date",
      secondaryStyles: "text-[12px] pt-1 whitespace-nowrap",
    },
    {
      header: "Location",
      key: "location",
      type: "text",
    },
    {
      header: "Application Deadline",
      key: "applicationDeadline",
      type: "text",
    },
    {
      header: "Seller Capacity",
      key: "sellerCapacity",
      type: "text",
    },
    {
      header: "Event Status",
      key: "status",
      type: "status",
      statusType: "application"
    },
    {
      header: "Note",
      key: "denialReason",
      type: "text",
      render: (value) => value ? (
        <span className="text-xs text-red-600 italic">{value}</span>
      ) : null,
    },
    {
      header: "",
      key: "action",
      type: "button",
      onClick: (id) => handleViewEvent(id),
    },
    {
      header: "",
      key: "payRegistration",
      type: "button",
      onClick: (id) => handlePayRegistration(id),
    },
  ];

  const tableData = filteredEvents.map(event => {
    let payRegistration = "";
    
    const showPaymentButton = 
      (event.paymentTiming === "upfront" && event.status === "Applied" && event.paymentStatus !== "Paid") ||
      (event.paymentTiming !== "upfront" && event.status === "Approved" && event.paymentStatus !== "Paid");
    
    if (showPaymentButton) {
      payRegistration = event.paymentTiming === "upfront" ? "Pay Now" : "Pay Now";
    }
    
    return {
      ...event,
      payRegistration
    };
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilters({
      category: "all",
      location: "",
      showGoodFitOnly: false,
      dateRange: "all",
      feeRange: "all",
      eventSize: "all",
      deadlineRange: "all",
    });
  };

  const activeFilterCount = [
    filters.category !== 'all',
    filters.location,
    filters.showGoodFitOnly,
    filters.dateRange !== 'all',
    filters.feeRange !== 'all',
    filters.eventSize !== 'all',
    filters.deadlineRange !== 'all',
  ].filter(Boolean).length;

  const hasActiveFilters = searchQuery || activeFilterCount > 0;

  const EventCard = ({ event, showFitBadge = false }) => (
    <div 
      className="bg-white rounded-xl border border-grey-200 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
      onClick={() => handleViewEvent(event.id)}
    >
      <div className="relative h-40 overflow-hidden">
        <img 
          src={event.image || picture_placeholder} 
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {showFitBadge && event.isGoodFit && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <SparklesIcon className="w-3 h-3" />
            Good Fit
          </div>
        )}
        {event.status !== "Not Applied" && (
          <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${
            event.status === "Approved" || event.status === "Registered" 
              ? "bg-primary-50 text-primary-700" 
              : event.status === "Applied" 
                ? "bg-blue-50 text-blue-700"
                : event.status === "Declined"
                  ? "bg-grey-100 text-grey-700"
                  : "bg-grey-100 text-grey-600"
          }`}>
            {event.status}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-grey-900 mb-1 line-clamp-1">{event.name}</h3>
        <div className="flex items-center text-grey-500 text-sm mb-2">
          <CalendarIcon className="w-4 h-4 mr-1" />
          {event.date}
        </div>
        <div className="flex items-center text-grey-500 text-sm mb-3">
          <LocationMarkerIcon className="w-4 h-4 mr-1" />
          <span className="line-clamp-1">{event.location || "Location TBD"}</span>
        </div>
        {event.matchedTypes && event.matchedTypes.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {event.matchedTypes.slice(0, 2).map((type, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded text-xs">
                {type}
              </span>
            ))}
          </div>
        )}
        {event.status === "Declined" && event.denialReason && (
          <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-xs font-medium text-red-700 mb-0.5">Application Declined</p>
            <p className="text-xs text-red-600">{event.denialReason}</p>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-grey-100">
          <span className="text-xs text-grey-400">
            Apply by {event.applicationDeadline || "TBD"}
          </span>
          <span className="text-xs font-medium text-primary-600 group-hover:text-primary-700">
            View Details →
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isLoading ? (
        <ModalLoadingState />
      ) : (
        <div className="p-3 sm:p-6">
          <div className="flex flex-col space-y-4">
            <ResponsiveTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="underline" />

            {activeTab === "history" ? (
              <HistoryTab
                pastEvents={pastEvents}
                loading={pastEventsLoading}
                onViewEvent={handleViewEvent}
              />
            ) : activeTab === "discover" ? (
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold text-grey-900">Discover Events</h1>
                    <p className="text-sm text-grey-600 mt-1">
                      Find events that match your business
                      {mpsProfile?.typeOfBusiness && (
                        <span className="ml-1 text-violet-600 font-medium">({mpsProfile.typeOfBusiness})</span>
                      )}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-grey-400" />
                      <input
                        type="text"
                        placeholder="Search events..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-grey-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition-colors ${
                        showFilters || hasActiveFilters
                          ? "border-primary-500 text-primary-600 bg-primary-50"
                          : "border-grey-200 text-grey-600 hover:bg-grey-50"
                      }`}
                    >
                      <FilterIcon className="w-4 h-4" />
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded-full min-w-[18px] text-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {showFilters && (
                  <div className="bg-grey-50 rounded-xl p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FilterIcon className="w-4 h-4 text-grey-500" />
                        <span className="text-sm font-medium text-grey-700">Filters</span>
                        {activeFilterCount > 0 && (
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                            {activeFilterCount} active
                          </span>
                        )}
                      </div>
                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-grey-600 hover:text-grey-800 hover:bg-grey-100 rounded-lg transition-colors"
                        >
                          <XIcon className="w-4 h-4" />
                          Clear all
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-grey-600 mb-1.5">Category</label>
                        <select
                          value={filters.category}
                          onChange={(e) => setFilters({...filters, category: e.target.value})}
                          className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="all">All Categories</option>
                          <option value="food">Food & Beverage</option>
                          <option value="craft">Arts & Crafts</option>
                          <option value="retail">Retail & Merchandise</option>
                          <option value="service">Services</option>
                          <option value="entertainment">Entertainment</option>
                          <option value="health">Health & Wellness</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-grey-600 mb-1.5">Location</label>
                        <LocationAutocomplete
                          value={filters.location}
                          onChange={(value) => setFilters({...filters, location: value})}
                          placeholder="City or state..."
                          mode="local"
                          compact={true}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-grey-600 mb-1.5">Event Date</label>
                        <select
                          value={filters.dateRange}
                          onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                          className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="all">Any Time</option>
                          <option value="thisWeek">This Week</option>
                          <option value="thisMonth">This Month</option>
                          <option value="next3Months">Next 3 Months</option>
                          <option value="later">Later</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-grey-600 mb-1.5">Registration Fee</label>
                        <select
                          value={filters.feeRange}
                          onChange={(e) => setFilters({...filters, feeRange: e.target.value})}
                          className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="all">Any Price</option>
                          <option value="free">Free</option>
                          <option value="under100">Under $100</option>
                          <option value="under250">$100 - $250</option>
                          <option value="under500">$250 - $500</option>
                          <option value="over500">Over $500</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-grey-600 mb-1.5">Event Size</label>
                        <select
                          value={filters.eventSize}
                          onChange={(e) => setFilters({...filters, eventSize: e.target.value})}
                          className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="all">Any Size</option>
                          <option value="small">Small (1-20 vendors)</option>
                          <option value="medium">Medium (21-50 vendors)</option>
                          <option value="large">Large (51-100 vendors)</option>
                          <option value="xlarge">Extra Large (100+ vendors)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-grey-600 mb-1.5">Apply By</label>
                        <select
                          value={filters.deadlineRange}
                          onChange={(e) => setFilters({...filters, deadlineRange: e.target.value})}
                          className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="all">Any Deadline</option>
                          <option value="urgent">Urgent (3 days)</option>
                          <option value="thisWeek">This Week</option>
                          <option value="thisMonth">This Month</option>
                          <option value="later">Later</option>
                        </select>
                      </div>
                      
                      <div className="sm:col-span-2 flex items-end">
                        <label className="flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg cursor-pointer hover:bg-violet-100 transition-colors w-full">
                          <input
                            type="checkbox"
                            checked={filters.showGoodFitOnly}
                            onChange={(e) => setFilters({...filters, showGoodFitOnly: e.target.checked})}
                            className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500 border-violet-300"
                          />
                          <SparklesIcon className="w-4 h-4 text-violet-500" />
                          <span className="text-sm font-medium text-violet-700">Good Fit only</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {goodFitEvents.length > 0 && !hasActiveFilters && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-1.5 rounded-lg">
                        <SparklesIcon className="w-4 h-4 text-white" />
                      </div>
                      <h2 className="text-lg font-semibold text-grey-900">Good Fit for You</h2>
                      <span className="text-sm text-grey-500">({goodFitEvents.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {goodFitEvents.slice(0, 6).map((event) => (
                        <EventCard key={event.id} event={event} showFitBadge={true} />
                      ))}
                    </div>
                    {goodFitEvents.length > 6 && (
                      <button 
                        onClick={() => setFilters({...filters, showGoodFitOnly: true})}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View all {goodFitEvents.length} matches →
                      </button>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-grey-900">
                      {hasActiveFilters ? "Filtered Events" : "All Upcoming Events"}
                    </h2>
                    <span className="text-sm text-grey-500">({filteredEvents.length})</span>
                  </div>
                  
                  {filteredEvents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredEvents.map((event) => (
                        <EventCard key={event.id} event={event} showFitBadge={true} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-grey-50 rounded-lg">
                      <SearchIcon className="w-12 h-12 text-grey-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-grey-900 mb-2">No Events Found</h3>
                      <p className="text-grey-500 mb-4">Try adjusting your search or filters</p>
                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-medium text-grey-900">
                      {activeTabLabel === "All Events"
                        ? "All Events"
                        : `Events ${activeTabLabel}`}
                      <span className="text-sm text-grey-500 ml-2">
                        ({filteredEvents.length} {filteredEvents.length === 1 ? "Event" : "Events"})
                      </span>
                    </h1>
                    <p className="text-sm text-grey-600 mt-1">Track your event applications and complete your registrations</p>
                  </div>
                </div>
                <PebbleTable
                  columns={columns}
                  data={tableData}
                  noDataTitle={`No ${activeTabLabel === "All Events"
                    ? ""
                    : activeTabLabel + " "
                    }Events`}
                />
              </>
            )}
          </div>
        </div>
      )}
      
      {showPaymentModal && selectedApplication && (
        <VendorPaymentModal
          isOpen={showPaymentModal}
          onClose={handlePaymentClose}
          applicationId={selectedApplication.applicationId}
          eventName={selectedApplication.eventName}
          registrationFee={selectedApplication.registrationFee}
        />
      )}
    </>
  );
}

export default EventContents;
